import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import useGetRequest from '../../../api/useGetRequest';
import useGetTourPlanningDetails from '../../../api/tours-planning/useGetTourPlanningDetails';
import useArchiveDeliveryDetails from '../../api/useArchiveDeliveryDetails';
import useScheduleOrders from '../../api/useScheduleOrders';
import AgvPageHeader, { PageHeaderButton } from '../../components/page-header';
import DropdownMenu from '../../components/dropdown-menu';
import { GroupHeader } from '../../components/data-grid';
import SelectionCheckbox from '../../components/selection-checkbox';
import GenericModal from '../../../components/agv-generic-modal';
import GenericTableSkeleton from '../../../components/agv-generic-table/genericTableSkeleton';
import { ORDER_BY_ENUM } from '../../../enums/orderby.enum';
import fleetApi from '../../../services/api.service';
import { buildUrlWithParams } from '../../../utils/url.utils';
import GridToolbar from './GridToolbar';
import ImportOrdersModal from './ImportOrdersModal';
import OrderDrawer from './OrderDrawer';
import {
  GROUP_BY,
  SORT,
  cleanClientName,
  formatWeight,
  getEntryDate,
  getOrderStatus,
  getToneClassName,
  getTransportDate,
  toOrderGroup,
} from './orders-pool.utils';
import './orders-pool.css';

moment.locale('fr');

const PAGE_SIZE = 24;
const EMPTY_SELECTION = { planningIds: new Set(), detailIds: new Set() };

// Onglets : BL du puits, ou BL supprimés depuis le TMS (consultation seule)
const VIEW = { ALL: 'ALL', DELETED: 'DELETED' };

// « BL » est invariable
const pluralize = (count, word) =>
  `${count} ${word}${count > 1 && word !== 'BL' ? 's' : ''}`;

export default function OrdersPool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const features = useSelector((state) => state.features);
  const canDeleteOrders = features?.TOUR_PLANNING?.ORDERS_POOL?.canDeleteOrders;

  // Sélection au BL près ; une ligne sans BL rattaché se sélectionne avec
  // son réceptionnaire (planningIds)
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [isFetchingIds, setIsFetchingIds] = useState(false);
  // Cible de la modale d'ordonnancement : { planningIds, detailIds, blCount }
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
  const [groupBy, setGroupBy] = useState(GROUP_BY.RECIPIENT_ONLY);
  const [view, setView] = useState(
    searchParams.get('view') === VIEW.DELETED ? VIEW.DELETED : VIEW.ALL
  );
  const isDeletedView = view === VIEW.DELETED;
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [openedRowKey, setOpenedRowKey] = useState(null);

  const [filters, setFilters] = useState({
    limit: PAGE_SIZE,
    offset: searchParams.get('offset') || 0,
    query: searchParams.get('query') || '',
    order:
      searchParams.get('order') === ORDER_BY_ENUM.ASC ||
      searchParams.get('order') === ORDER_BY_ENUM.DESC
        ? searchParams.get('order')
        : ORDER_BY_ENUM.DESC,
    sort:
      searchParams.get('sort') === SORT.CREATED_AT ? SORT.CREATED_AT : SORT.ID,
    created_date: searchParams.get('created_date') || '',
    client_name: searchParams.get('client_name') || '',
    statuses: searchParams.get('statuses') || '',
  });

  /**
   * Met à jour un filtre ; tout changement autre que la page revient à la
   * première page
   */
  const handleUpdateParam = useCallback((key, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, [key]: value };
      if (key !== 'offset') newFilters.offset = 0;
      return newFilters;
    });
  }, []);

  // Les filtres vides ne sont envoyés ni à l'API ni dans l'URL
  const activeFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '')
      ),
    [filters]
  );

  // L'URL reste la source de vérité des filtres
  useEffect(() => {
    navigate(
      {
        search: new URLSearchParams({
          ...activeFilters,
          ...(isDeletedView ? { view } : {}),
        }).toString(),
      },
      { replace: true }
    );
  }, [activeFilters, isDeletedView, view, navigate]);

  // Onglet « Supprimés » : le filtre de statut est remplacé par DELETED
  const url = useMemo(
    () =>
      buildUrlWithParams('/tours-planning', {
        ...activeFilters,
        ...(isDeletedView ? { statuses: VIEW.DELETED } : {}),
        unit: 'BL',
      }),
    [activeFilters, isDeletedView]
  );

  const { data, isLoading, isFirstLoad, mutate } = useGetRequest(url, 300);
  const { data: filtersData } = useGetRequest('/tours-planning/filters', 300);
  // Import Excel (ORDERS_POOL.MANUAL_CREATION) : l'API dit s'il est ouvert au client
  const { data: poolConfig } = useGetRequest('/orders-pool/config');
  const isImportEnabled = poolConfig?.importEnabled === true;
  const [isImportOpen, setIsImportOpen] = useState(false);

  const { scheduleOrders, isScheduling } = useScheduleOrders();
  const { archiveDeliveryDetails } = useArchiveDeliveryDetails();
  const {
    isLoading: isDetailsLoading,
    data: detailsData,
    fetchDetails,
    reset: resetDetails,
  } = useGetTourPlanningDetails();

  const groups = useMemo(
    () => (data?.items || []).map(toOrderGroup),
    [data?.items]
  );

  // Sélection de tous les résultats (au-delà de la page) via /ids
  const selectAllResults = async () => {
    // eslint-disable-next-line no-unused-vars
    const { limit, offset, sort, order, ...idsFilters } = activeFilters;
    setIsFetchingIds(true);
    try {
      const response = await fleetApi.get(
        buildUrlWithParams('/tours-planning/ids', { ...idsFilters, unit: 'BL' })
      );
      const ids = response.data?.ids || [];
      setSelection({ planningIds: new Set(), detailIds: new Set(ids) });
    } catch (error) {
      toast.error('Impossible de sélectionner tous les résultats');
    } finally {
      setIsFetchingIds(false);
    }
  };

  const clientNames = useMemo(
    () => (filtersData?.client_names || []).map(cleanClientName),
    [filtersData]
  );
  const statuses = useMemo(
    () => (filters.statuses ? filters.statuses.split(',') : []),
    [filters.statuses]
  );
  const totalItems = data?.pagination_info?.total_items || 0;
  const visibleBlCount = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const openedEntry = useMemo(() => {
    for (const group of groups) {
      const row = group.rows.find((r) => r.key === openedRowKey);
      if (row) return { group, row };
    }
    return null;
  }, [groups, openedRowKey]);

  // ---------------------------------------------------------------------------
  // Sélection
  // ---------------------------------------------------------------------------

  const isRowSelected = (group, row) =>
    selection.planningIds.has(group.id) ||
    (row.detailId !== null && selection.detailIds.has(row.detailId));

  /**
   * Coche ou décoche des lignes de BL
   */
  const setRowsSelected = (entries, checked) => {
    setSelection((prev) => {
      const planningIds = new Set(prev.planningIds);
      const detailIds = new Set(prev.detailIds);

      entries.forEach(({ group, row }) => {
        if (!group.isSchedulable) return;
        if (row.detailId === null) {
          if (checked) planningIds.add(group.id);
          else planningIds.delete(group.id);
          return;
        }
        if (!checked && planningIds.has(group.id)) {
          planningIds.delete(group.id);
          group.rows.forEach(
            (other) => other.detailId !== null && detailIds.add(other.detailId)
          );
        }
        if (checked) detailIds.add(row.detailId);
        else detailIds.delete(row.detailId);
      });

      return { planningIds, detailIds };
    });
  };

  const getSelectionState = (entries) => {
    const selectable = entries.filter(({ group }) => group.isSchedulable);
    if (selectable.length === 0) return 'none';
    const count = selectable.filter(({ group, row }) =>
      isRowSelected(group, row)
    ).length;
    if (count === 0) return 'none';
    return count === selectable.length ? 'all' : 'some';
  };

  const allEntries = useMemo(
    () => groups.flatMap((group) => group.rows.map((row) => ({ group, row }))),
    [groups]
  );
  const pageSelectionState = getSelectionState(allEntries);

  const clearSelection = () => setSelection(EMPTY_SELECTION);

  const selectedCount = selection.detailIds.size + selection.planningIds.size;
  const hasSelection = selectedCount > 0;

  const canSelectAllResults =
    pageSelectionState === 'all' && totalItems > selectedCount;

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const openRow = (row) => {
    setOpenedRowKey(row.key);
    fetchDetails(row.planningId);
  };

  const closeDrawer = useCallback(() => {
    setOpenedRowKey(null);
    resetDetails();
  }, [resetDetails]);

  const handleViewChange = (nextView) => {
    if (nextView === view) return;
    setView(nextView);
    setSelection(EMPTY_SELECTION);
    closeDrawer();
    handleUpdateParam('offset', 0);
  };

  const openScheduleModal = (target) => {
    if (target.planningIds.length + target.detailIds.length === 0) return;
    setScheduleTarget(target);
  };

  const scheduleSelection = () =>
    openScheduleModal({
      planningIds: [...selection.planningIds],
      detailIds: [...selection.detailIds],
      blCount: selectedCount,
    });

  // Une ligne seule : son BL (ou son réceptionnaire si elle n'a pas de BL)
  const scheduleRow = (group, row) =>
    openScheduleModal(
      row.detailId === null
        ? { planningIds: [group.id], detailIds: [], blCount: 1 }
        : { planningIds: [], detailIds: [row.detailId], blCount: 1 }
    );

  // Tous les BL du réceptionnaire, y compris ceux des autres pages
  const scheduleRecipient = (group) =>
    openScheduleModal({
      planningIds: [group.id],
      detailIds: [],
      blCount: group.totalBlCount,
    });

  const closeScheduleModal = () => {
    setScheduleTarget(null);
    setSelectedDeliveryDate(null);
  };

  const handleConfirmScheduling = async () => {
    if (!scheduleTarget || !selectedDeliveryDate) return;
    try {
      const result = await scheduleOrders({
        tourPlanningIds: scheduleTarget.planningIds,
        deliveryDetailIds: scheduleTarget.detailIds,
        deliveryDate: moment(selectedDeliveryDate).format('YYYY-MM-DD'),
      });
      toast.success(result?.message || 'Envoyé en ordonnancement');

      // Les BL ordonnancés quittent le puits : on les retire de la sélection
      setSelection((prev) => ({
        planningIds: new Set(
          [...prev.planningIds].filter(
            (id) => !scheduleTarget.planningIds.includes(id)
          )
        ),
        detailIds: new Set(
          [...prev.detailIds].filter(
            (id) => !scheduleTarget.detailIds.includes(id)
          )
        ),
      }));
      closeScheduleModal();
      closeDrawer();
      mutate();
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Supprime un BL du puits : il est archivé côté API (rien n'est effacé, la
   * suppression est tracée)
   */
  const handleDeleteRow = async (group, row) => {
    if (row.detailId === null) return;
    if (
      !window.confirm(
        `Supprimer le BL ${row.blKey} (${group.recipient}) du puits de commandes ?`
      )
    ) {
      return;
    }

    try {
      await archiveDeliveryDetails([row.detailId]);
      toast.success(`BL ${row.blKey} supprimé du puits`);
      setRowsSelected([{ group, row }], false);
      if (openedRowKey === row.key) closeDrawer();
      mutate();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getRowMenuItems = (group, row, { fromDrawer = false } = {}) => [
    ...(fromDrawer
      ? []
      : [{ label: 'Voir la fiche du BL', onClick: () => openRow(row) }]),
    // BL supprimé : consultation seule
    ...(isDeletedView ? [] : getRowActionItems(group, row)),
  ];

  const getRowActionItems = (group, row) => [
    {
      label:
        row.detailId === null
          ? 'Envoyer en ordonnancement…'
          : 'Envoyer ce BL en ordonnancement…',
      disabled: !group.isSchedulable,
      onClick: () => scheduleRow(group, row),
    },
    ...(group.totalBlCount > 1
      ? [
          {
            label: 'Envoyer tous les BL du réceptionnaire…',
            hint: `${group.totalBlCount} BL`,
            disabled: !group.isSchedulable,
            onClick: () => scheduleRecipient(group),
          },
        ]
      : []),
    ...(canDeleteOrders && group.isSchedulable && row.detailId !== null
      ? [
          { separator: true },
          {
            label: 'Supprimer le BL…',
            danger: true,
            onClick: () => handleDeleteRow(group, row),
          },
        ]
      : []),
  ];

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  /**
   * Tri par colonne : un clic sur la colonne active inverse l'ordre, un clic
   * sur une autre colonne la trie du plus récent au plus ancien
   */
  const handleSort = (sort) => {
    if (filters.sort === sort) {
      handleUpdateParam(
        'order',
        filters.order === ORDER_BY_ENUM.DESC
          ? ORDER_BY_ENUM.ASC
          : ORDER_BY_ENUM.DESC
      );
      return;
    }
    setFilters((prev) => ({
      ...prev,
      sort,
      order: ORDER_BY_ENUM.DESC,
      offset: 0,
    }));
  };

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  /**
   * Groupes affichés : par réceptionnaire (et date), par jour d'entrée dans
   * le puits (sur la page courante), ou liste à plat
   */
  const displayGroups = useMemo(() => {
    // L'en-tête d'un réceptionnaire ne reprend pas ce que les colonnes
    // affichent déjà (adresse, date d'entrée) ni le nombre de BL (lignes)
    if (groupBy === GROUP_BY.RECIPIENT_ONLY) {
      const byRecipient = new Map();
      groups.forEach((group) => {
        const key = group.recipientCode || group.recipient;
        if (!byRecipient.has(key)) {
          byRecipient.set(key, {
            key: `recipient-only-${key}`,
            title: group.recipient,
            // Code identique au nom (destinataire sans code) : non répété
            parts: [
              group.recipientCode !== group.recipient
                ? group.recipientCode
                : null,
            ],
            isRecipient: true,
            entries: [],
          });
        }
        byRecipient
          .get(key)
          .entries.push(...group.rows.map((row) => ({ group, row })));
      });
      return [...byRecipient.values()];
    }

    if (groupBy === GROUP_BY.RECIPIENT) {
      return groups.map((group) => ({
        key: `recipient-${group.id}`,
        title: group.recipient,
        parts: [
          group.recipientCode !== group.recipient ? group.recipientCode : null,
          group.date
            ? `Livraison souhaitée le ${moment(group.date).format('DD/MM/YYYY')}`
            : null,
        ],
        isRecipient: true,
        entries: group.rows.map((row) => ({ group, row })),
      }));
    }

    if (groupBy === GROUP_BY.ENTRY_DATE) {
      const byDay = new Map();
      allEntries.forEach((entry) => {
        const entryDate = getEntryDate(entry.row.createdAt);
        const key = entryDate.day || 'none';
        if (!byDay.has(key)) {
          byDay.set(key, {
            key: `day-${key}`,
            title: entryDate.day
              ? `Entrés le ${moment(entryDate.day).format('dddd D MMMM YYYY')}`
              : "Sans date d'entrée",
            parts: [],
            entries: [],
          });
        }
        byDay.get(key).entries.push(entry);
      });
      return [...byDay.values()];
    }

    return [{ key: 'all', flat: true, entries: allEntries }];
  }, [groupBy, groups, allEntries]);

  const renderRow = ({ group, row }) => {
    const isSelected = isRowSelected(group, row);
    const status = getOrderStatus(group.element);
    const entryDate = getEntryDate(row.createdAt);
    const transportDate = getTransportDate(row.date);

    return (
      <div
        key={row.key}
        className={`agv-dg-row ${isSelected ? 'agv-dg-row--selected' : ''} ${
          openedRowKey === row.key ? 'agv-dg-row--opened' : ''
        }`}
        onClick={() => openRow(row)}
      >
        <div className="agv-dg-cell agv-dg-cell--select">
          <SelectionCheckbox
            checked={isSelected}
            disabled={!group.isSchedulable}
            onChange={(checked) => setRowsSelected([{ group, row }], checked)}
            title={
              group.isSchedulable
                ? 'Sélectionner le BL'
                : `${status.label} : déjà sorti du puits`
            }
          />
        </div>
        <div className="agv-dg-cell op-cell--order">
          <button
            type="button"
            className="agv-dg-bl-link"
            onClick={(e) => {
              e.stopPropagation();
              openRow(row);
            }}
          >
            {row.blKey}
          </button>
          {group.plannedVehicle && !isDeletedView && (
            <span
              className="op-planned-vehicle"
              title={`Camion prévu par le fichier : la commande sera affectée d'office au camion ${group.plannedVehicle} à l'envoi en ordonnancement`}
            >
              {group.plannedVehicle}
            </span>
          )}
        </div>
        <div className="agv-dg-cell op-cell--recipient" title={group.recipient}>
          {group.recipient}
        </div>
        <div
          className={`agv-dg-cell op-cell--status ${isDeletedView ? 'op-cell--deleted' : ''}`}
        >
          <span className="op-status-cell">
            <span>
              <span
                className="agv-dg-dot"
                style={{ backgroundColor: status.color }}
              />
              {status.label}
            </span>
            {isDeletedView && (group.archivedAt || group.archivedBy) && (
              <span
                className="agv-dg-cell__secondary"
                title={group.archivedBy || undefined}
              >
                {[
                  group.archivedAt &&
                    moment.utc(group.archivedAt).local().format('DD/MM HH:mm'),
                  group.archivedBy,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </span>
        </div>
        <div className="agv-dg-cell op-cell--weight">
          {formatWeight(row.weight)}
        </div>
        <div className="agv-dg-cell op-cell--items">
          {row.items.length} art.
        </div>
        <div className="agv-dg-cell op-cell--address">
          <span>{group.city || '—'}</span>
          <span className="agv-dg-cell__secondary">{group.street}</span>
        </div>
        <div className="agv-dg-cell op-cell--delivery">
          <span className={getToneClassName(transportDate.tone)}>
            {transportDate.label}
          </span>
          {row.timeSlot && (
            <span className="agv-dg-cell__secondary">{row.timeSlot}</span>
          )}
        </div>
        <div className="agv-dg-cell op-cell--dates">
          <span>{entryDate.label}</span>
          {entryDate.time && (
            <span className="agv-dg-cell__secondary">{entryDate.time}</span>
          )}
        </div>
        <div className="agv-dg-cell agv-dg-cell--spacer" />
        <div
          className="agv-dg-cell agv-dg-cell--more"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu
            width={300}
            items={getRowMenuItems(group, row)}
            renderTrigger={({ toggle }) => (
              <button
                type="button"
                className="agv-dg-icon-btn"
                onClick={toggle}
                title="Actions"
              >
                ⋯
              </button>
            )}
          />
        </div>
      </div>
    );
  };

  const renderDisplayGroup = (displayGroup) => {
    if (displayGroup.flat) {
      return displayGroup.entries.map(renderRow);
    }

    const weight = displayGroup.entries.reduce(
      (sum, { row }) => sum + row.weight,
      0
    );
    const selectionState = getSelectionState(displayGroup.entries);
    const isCollapsed = collapsedGroups.has(displayGroup.key);

    return (
      <React.Fragment key={displayGroup.key}>
        <GroupHeader
          title={displayGroup.title}
          parts={displayGroup.parts}
          totals={
            displayGroup.isRecipient
              ? formatWeight(weight)
              : `${displayGroup.entries.length} BL · ${formatWeight(weight)}`
          }
          collapsed={isCollapsed}
          onToggle={() => toggleGroupCollapse(displayGroup.key)}
          selection={{
            state: selectionState,
            disabled: displayGroup.entries.every(
              ({ group }) => !group.isSchedulable
            ),
            onChange: (checked) =>
              setRowsSelected(displayGroup.entries, checked),
            title: `Sélectionner les ${displayGroup.entries.length} BL du groupe`,
          }}
        />
        {!isCollapsed && displayGroup.entries.map(renderRow)}
      </React.Fragment>
    );
  };

  const renderGridBody = () => {
    if (isFirstLoad && isLoading) {
      return (
        <div className="agv-dg-grid__loading">
          <GenericTableSkeleton numberOfRows={8} />
        </div>
      );
    }

    if (groups.length === 0) {
      return (
        <div className="agv-dg-empty">
          <span className="agv-dg-empty__icon">○</span>
          <span className="agv-dg-empty__title">
            {isDeletedView ? 'Aucun BL supprimé' : 'Aucun BL dans le puits'}
          </span>
          <span className="agv-dg-empty__text">
            {filters.query || filters.client_name || filters.created_date
              ? 'Aucun BL ne correspond aux filtres en cours.'
              : isDeletedView
                ? 'Les BL supprimés du puits depuis le TMS apparaîtront ici.'
                : ''}
          </span>
        </div>
      );
    }

    return (
      <>
        {displayGroups.map(renderDisplayGroup)}
        <div className="agv-dg-end-of-list">
          {visibleBlCount} BL sur cette page · {pluralize(totalItems, 'BL')} au
          total
        </div>
      </>
    );
  };

  const renderSortHeader = (sort, label, className) => {
    const isActive = filters.sort === sort;
    return (
      <button
        type="button"
        className={`agv-dg-cell ${className} agv-dg-head-sort`}
        onClick={() => handleSort(sort)}
        title={
          isActive && filters.order === ORDER_BY_ENUM.ASC
            ? 'Plus anciens en premier'
            : 'Plus récents en premier'
        }
      >
        {label}
        {isActive && (
          <span className="agv-dg-head-sort__arrow">
            {filters.order === ORDER_BY_ENUM.DESC ? '↓' : '↑'}
          </span>
        )}
      </button>
    );
  };

  const scheduleCount = scheduleTarget?.blCount || 0;
  const scheduleLabel = pluralize(scheduleCount, 'BL');

  return (
    <div className="op-page">
      <AgvPageHeader
        icon={faInbox}
        breadcrumb={['Pilotage', 'Puits de commandes']}
        title="Puits de commandes"
        count={totalItems}
        activeTab={view}
        onTabChange={handleViewChange}
        tabs={[
          { key: VIEW.ALL, icon: '☰', label: 'Tous les BL' },
          { key: VIEW.DELETED, icon: '✕', label: 'Supprimés' },
        ]}
        actions={
          !isDeletedView && (
            <>
              <DropdownMenu
                width={320}
                items={[
                  {
                    label: 'Sélectionner toute la page',
                    disabled: allEntries.length === 0,
                    onClick: () => setRowsSelected(allEntries, true),
                  },
                  {
                    label: `Sélectionner les ${totalItems} BL`,
                    disabled: totalItems === 0 || isFetchingIds,
                    onClick: selectAllResults,
                  },
                  { separator: true },
                  {
                    label: 'Envoyer la sélection en ordonnancement…',
                    disabled: !hasSelection,
                    onClick: scheduleSelection,
                  },
                ]}
                renderTrigger={({ toggle }) => (
                  <PageHeaderButton chevron onClick={toggle}>
                    Actions
                  </PageHeaderButton>
                )}
              />
              {isImportEnabled && (
                <PageHeaderButton
                  primary
                  glyph="↑"
                  onClick={() => setIsImportOpen(true)}
                >
                  Importer Excel
                </PageHeaderButton>
              )}
            </>
          )
        }
      />

      <div className="agv-dg-grid">
        <GridToolbar
          query={filters.query}
          onQueryChange={(value) => handleUpdateParam('query', value)}
          clientName={filters.client_name}
          onClientNameChange={(value) =>
            handleUpdateParam('client_name', value)
          }
          clientNames={clientNames}
          createdDate={filters.created_date}
          onCreatedDateChange={(value) =>
            handleUpdateParam('created_date', value)
          }
          statuses={statuses}
          showStatusFilter={!isDeletedView}
          onStatusesChange={(values) =>
            handleUpdateParam(
              'statuses',
              values.length === 1 && values[0] === 'PLANNED'
                ? ''
                : values.join(',')
            )
          }
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          paginationInfo={data?.pagination_info}
          onOffsetChange={(value) => handleUpdateParam('offset', value)}
        />

        <div className="agv-dg-grid__scroll">
          <div className="agv-dg-grid__table">
            <div className="agv-dg-grid__head">
              <div className="agv-dg-cell agv-dg-cell--select">
                <SelectionCheckbox
                  disabled={isDeletedView}
                  checked={pageSelectionState === 'all'}
                  indeterminate={pageSelectionState === 'some'}
                  onChange={(checked) => setRowsSelected(allEntries, checked)}
                  title="Sélectionner la page"
                />
              </div>
              {renderSortHeader(SORT.ID, 'N° BL', 'op-cell--order')}
              <div className="agv-dg-cell op-cell--recipient">
                Réceptionnaire
              </div>
              <div
                className={`agv-dg-cell op-cell--status ${isDeletedView ? 'op-cell--deleted' : ''}`}
              >
                {isDeletedView ? 'Suppression' : 'Statut'}
              </div>
              <div className="agv-dg-cell op-cell--weight">Poids</div>
              <div className="agv-dg-cell op-cell--items">Articles</div>
              <div className="agv-dg-cell op-cell--address">Adresse</div>
              <div className="agv-dg-cell op-cell--delivery">Livraison</div>
              {renderSortHeader(
                SORT.CREATED_AT,
                'Entrée puits',
                'op-cell--dates'
              )}
              <div className="agv-dg-cell agv-dg-cell--spacer" />
              <div className="agv-dg-cell agv-dg-cell--more" />
            </div>
            <div
              className={`agv-dg-grid__body ${isLoading ? 'agv-dg-grid__body--loading' : ''}`}
            >
              {renderGridBody()}
            </div>
          </div>
        </div>

        {hasSelection && (
          <div className="agv-dg-selection-bar">
            <SelectionCheckbox
              checked
              onChange={clearSelection}
              title="Vider la sélection"
            />
            <span className="agv-dg-selection-bar__count">
              {pluralize(selectedCount, 'BL')} sélectionné
              {selectedCount > 1 ? 's' : ''}
            </span>
            <span className="agv-dg-divider" />
            <button
              type="button"
              className="agv-dg-link agv-dg-link--strong"
              onClick={scheduleSelection}
              disabled={isScheduling}
            >
              Envoyer en ordonnancement
            </button>
            {canSelectAllResults && (
              <>
                <span className="agv-dg-divider" />
                <button
                  type="button"
                  className="agv-dg-link"
                  onClick={selectAllResults}
                  disabled={isFetchingIds}
                >
                  Sélectionner les {totalItems} BL
                </button>
              </>
            )}
            <span className="agv-dg-divider" />
            <button
              type="button"
              className="agv-dg-icon-btn"
              onClick={clearSelection}
              title="Vider la sélection"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {openedEntry && (
        <OrderDrawer
          group={openedEntry.group}
          row={openedEntry.row}
          details={detailsData}
          isLoadingDetails={isDetailsLoading}
          menuItems={getRowMenuItems(openedEntry.group, openedEntry.row, {
            fromDrawer: true,
          })}
          onClose={closeDrawer}
        />
      )}

      <GenericModal
        isOpen={Boolean(scheduleTarget)}
        setIsOpen={(open) => !open && closeScheduleModal()}
        title="Sélectionner la date de livraison"
        mainAction={handleConfirmScheduling}
        mainActionText="Confirmer"
        mainActionDisabled={!selectedDeliveryDate}
        mainActionIsLoading={isScheduling}
        secondaryAction={closeScheduleModal}
        secondaryActionText="Annuler"
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-items-center">
          <p className="tw-text-sm tw-text-gray-600 tw-text-center">
            Veuillez sélectionner la date à laquelle{' '}
            {scheduleCount > 1 ? 'les' : 'le'} <strong>{scheduleLabel}</strong>{' '}
            sélectionné
            {scheduleCount > 1 ? 's' : ''} doi{scheduleCount > 1 ? 'vent' : 't'}{' '}
            être livré{scheduleCount > 1 ? 's' : ''}.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center">
            <label className="tw-font-medium tw-text-sm">
              Date de livraison souhaitée
            </label>
            <ReactDatePicker
              selected={selectedDeliveryDate}
              onChange={(date) => setSelectedDeliveryDate(date)}
              locale="fr"
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              inline
            />
          </div>
          {selectedDeliveryDate && (
            <div className="tw-bg-blue-50 tw-border-2 tw-border-blue-200 tw-rounded-lg tw-p-4 tw-w-full">
              <p className="tw-text-sm tw-text-gray-700 tw-text-center">
                Date sélectionnée :{' '}
                <strong>
                  {moment(selectedDeliveryDate).format('dddd D MMMM YYYY')}
                </strong>
              </p>
            </div>
          )}
        </div>
      </GenericModal>

      {isImportEnabled && (
        <ImportOrdersModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImported={() => mutate()}
        />
      )}
    </div>
  );
}
